import produce, { Draft } from 'immer'
import { AxiosResponse } from 'axios'

import { ApiLocalizationWithStatus, ProductsActionTypes, ProductsState } from '../types/products'
import {
  ADD_BRAND,
  CLEAR_NEW_BRAND,
  SET_BATCH_PHOTOS,
  SET_BATCH_PRODUCTS,
  SET_BRAND_SEARCH_MODE,
  SET_BRANDS,
  SET_DELETE_LOADING,
  SET_LOCALIZATION_STATUS,
  SET_LOCALIZATIONS,
  SET_MARK_REVIEWED_LOADING,
  SET_MERGE_PRODUCTS_LOADING,
  SET_NOTICES,
  SET_NOTICES_REVIEWED_STATUS,
  SET_PHOTOS,
  SET_PRODUCTS,
} from '../actions/products'
import { ApiLocalization } from '../../types'

const photosResponseParser = (responseArr: AxiosResponse[]) => {
  const productPhotos: { [key: string]: any } = {}

  responseArr.forEach((response: any) => {
    productPhotos[JSON.parse(response.config.data).productId] = response.data.photos
  })

  return productPhotos
}

const initialState: ProductsState = {
  products: [],
  batchProducts: {},
  productPhotos: {},
  batchProductPhotos: {},
  notices: {},
  noticesLoading: {},
  productIdToUuid: {},
  brands: [],
  newBrand: null,
  brandSearchMode: 'phonetic',
  localizations: {},
  productId: '',
  productUuid: '',
}

export const productsReducer = produce(
  (state: Draft<ProductsState>, action: ProductsActionTypes) => {
    switch (action.type) {
      case SET_PRODUCTS:
        if (action.payload !== null) {
          if (action.payload.batchId) {
            state.products = state.batchProducts[action.payload.batchId] || []
          } else {
            state.products = action.payload.products
          }
        } else {
          state.products = []
        }
        return

      case SET_BRANDS:
        if (action.payload !== null) {
          if (action.payload.brands) {
            state.brands = [...action.payload.brands]
          }
        }
        return

      case ADD_BRAND:
        if (action.payload !== null) {
          if (action.payload.brand) {
            state.brands = [...state.brands, action.payload.brand]
            state.newBrand = action.payload.brand
          }
        }
        return

      case CLEAR_NEW_BRAND:
        state.newBrand = null
        return

      case SET_BRAND_SEARCH_MODE:
        state.brandSearchMode = action.payload.mode
        return

      case SET_NOTICES:
        if (action.payload !== null) {
          state.notices = action.payload.notices
          state.productIdToUuid = action.payload.productIdToUuid
          state.noticesLoading = action.payload.noticesLoading
        } else {
          state.notices = {}
          state.noticesLoading = {}
          state.productIdToUuid = {}
        }
        return

      case SET_LOCALIZATIONS:
        if (action.payload !== null) {
          state.localizations = {
            ...state.localizations,
            [action.payload.productId]: action.payload.localizations,
          }
          state.productUuid = action.payload.productUuid
          state.productId = action.payload.productId
        }
        return

      case SET_NOTICES_REVIEWED_STATUS:
        if (action.payload !== null) {
          action.payload.notices.forEach((payload) => {
            state.notices[payload.product_uuid].reviewed = true
            state.noticesLoading[payload.product_uuid].markReviewed = false
          })
        }
        return

      case SET_MERGE_PRODUCTS_LOADING:
        if (action.payload !== null) {
          state.noticesLoading[action.payload.product_uuid].merge = action.payload.loading
        }
        return

      case SET_DELETE_LOADING:
        if (action.payload !== null) {
          // the delete action uses productId rather than productUuid,
          // but our loading tracker object uses UUID, so we use the mapping between the two
          // to get the information we need
          const uuid = state.productIdToUuid[action.payload.productId]
          state.noticesLoading[uuid].delete = action.payload.deleteIsLoading

          // mark as already pending deletion without doing a full re-fetch of notices
          state.notices[uuid].product_removal_initiated = action.payload.removalInitiated
            ? 'now'
            : null
        }
        return

      case SET_MARK_REVIEWED_LOADING:
        if (action.payload !== null) {
          const toMarkLoading = action.payload.notices
            .filter((x) => x.mark_reviewed_loading)
            .map((x) => x.product_uuid)
          toMarkLoading.forEach((productUUID: string) => {
            state.noticesLoading[productUUID].markReviewed = true
          })
        }
        return

      case SET_BATCH_PRODUCTS:
        if (action.payload !== null) {
          state.batchProducts[action.payload.batchId] = action.payload.products
        } else {
          state.batchProducts = {}
        }
        return

      case SET_LOCALIZATION_STATUS:
        if (action.payload !== null) {
          if (action.payload.newProductLocalizationId) {
            const { newProductLocalizationId, updated, success, failure, country, subdivision } =
              action.payload
            // means we made a new one
            state.localizations[state.productId] = [
              ...state.localizations[state.productId].filter(
                (each) => each.country !== country || each.subdivision !== subdivision
              ),
              {
                ...state.localizations[state.productId].find(
                  (each) => each.country === country && each.subdivision === subdivision
                ),
                id: newProductLocalizationId,
                updated,
                success,
                failure,
              } as ApiLocalizationWithStatus,
            ]
          } else {
            // updated old product
            const { updated, success, failure, originalLocalizationId } = action.payload
            state.localizations[state.productId] = [
              ...state.localizations[state.productId].filter(
                (each: ApiLocalization) => each.id !== originalLocalizationId
              ),
              {
                ...state.localizations[state.productId].find(
                  (each: ApiLocalization) => each.id === originalLocalizationId
                ),
                updated,
                success,
                failure,
              } as ApiLocalizationWithStatus,
            ]
          }
        }
        return

      case SET_PHOTOS:
        if (action.payload !== null) {
          if (action.payload.batchId) {
            state.productPhotos = state.batchProductPhotos[action.payload.batchId] || {}
          } else {
            state.productPhotos = photosResponseParser(action.payload.productPhotosResponse)
          }
        } else {
          state.productPhotos = {}
        }
        return

      case SET_BATCH_PHOTOS:
        if (action.payload !== null) {
          state.batchProductPhotos[action.payload.batchId] = photosResponseParser(
            action.payload.productPhotosResponse
          )
        } else {
          state.batchProductPhotos = {}
        }

        return
    }
  },
  initialState
)
